<template>
  <div class="dashboard-charts">
    <div class="charts-container">
      <div class="dashboard-header">
        <h2 class="dashboard-title">{{ $t('dashboard.title') }}</h2>
        <div class="period-selector">
          <v-select v-if="selectedPeriod === 'month'" v-model="selectedAccount" :items="['All', 'Demo', 'Live']"
            :label="$t('selectAccount')" dense outlined class="month-selector mr-4" hide-details></v-select>
          <v-select v-if="selectedPeriod === 'month'" v-model="selectedMonth" :items="timePeriods"
            :label="$t('dashboard.periods.selectPeriod')" dense outlined class="month-selector ml-auto"
            hide-details></v-select>
        </div>
      </div>
      <div class="metrics-grid">
        <div class="metric-card">
          <div class="metric-icon">
            <v-icon color="success">fas fa-chart-line</v-icon>
          </div>
          <div class="metric-content">
            <p class="metric-label">{{ $t('dashboard.metrics.totalTrades') }}</p>
            <v-progress-circular v-if="isLoading" indeterminate color="primary" size="32"></v-progress-circular>
            <h3 v-else class="metric-value">{{ dataForMetricsAndCharts.activeTraders }}/<span style="font-size: 16px; font-weight: normal;">{{
              ((dataForMetricsAndCharts.activeTraders / $store.state.clubMembers.length)*100).toFixed() }}% total</span></h3>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-icon">
            <v-icon color="primary">fas fa-dollar-sign</v-icon>
          </div>
          <div class="metric-content">
            <p class="metric-label">{{ $t('dashboard.metrics.profitLoss') }}</p>
            <v-progress-circular v-if="isLoading" indeterminate color="primary" size="32"></v-progress-circular>
            <h3 v-else class="metric-value">{{ dataForMetricsAndCharts.liveAccounts }}</h3>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-icon">
            <v-icon color="warning">fas fa-clock</v-icon>
          </div>
          <div class="metric-content">
            <p class="metric-label">{{ $t('dashboard.metrics.avgDuration') }}</p>
            <v-progress-circular v-if="isLoading" indeterminate color="primary" size="32"></v-progress-circular>
            <h3 v-else class="metric-value">{{ dataForMetricsAndCharts.demoAccounts }}</h3>
          </div>
        </div>
        <div class="metric-card">
          <div class="metric-content">
            <p class="metric-label">{{ $t('dashboard.metrics.demoToLive') }}</p>
            <v-progress-circular v-if="isLoading" indeterminate color="primary" size="32"></v-progress-circular>
            <h3 v-else class="metric-value">-%</h3>
          </div>
        </div>
      </div>

      <div class="circular-charts">
        <div class="chart-card">
          <div class="chart-header">
            <h3>{{ $t('dashboard.charts.successRate.title') }}</h3>
            <v-chip class="d-none" small color="success" text-color="white">+5.2%</v-chip>
          </div>
          <div v-if="!dataForMetricsAndCharts" class="no-data-message">
            {{ $t('dashboard.charts.noData') }}
          </div>
          <apexchart v-else type="pie" :options="successChartOptions"
            :series="[dataForMetricsAndCharts.profitableTraders, dataForMetricsAndCharts.notProfitableTraders]"
            height="300" />
        </div>
        <div class="chart-card">
          <div class="chart-header">
            <h3>{{ $t('dashboard.charts.topAssets.title') }}</h3>
          </div>
          <div v-if="!hasAssetsData" class="no-data-message">
            {{ $t('dashboard.charts.noData') }}
          </div>
          <apexchart v-else type="pie" :options="topAssetsChartOptions" :series="getTop5AssetsPercentages"
            height="300" />
        </div>
      </div>

      <div class="histogram-card blurred-chart">
        <div class="chart-header">
          <h3>{{ $t('dashboard.charts.performance.title') }}</h3>
        </div>
        <apexchart style="filter: blur(5px);" type="bar" :options="histogramOptions" :series="histogramSeries"
          height="300" />
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import Repository from '../../repositories/club';

export default {
  name: 'DashboardCharts',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    setLoading: Function
  },
  data() {
    return {
      selectedPeriod: 'month',
      selectedMonth: '1',
      selectedAccount: 'All',
      timePeriods: [
        { text: this.$t('dashboard.periods.oneMonthAgo'), value: '1' },
        { text: this.$t('dashboard.periods.threeMonthsAgo'), value: '3' },
        { text: this.$t('dashboard.periods.sixMonthsAgo'), value: '6' }
      ],
      dataForMetricsAndCharts: undefined,
      isLoading: false,
    }
  },
  computed: {
    clubColor() {
      return `#${this.$store.state.clubMember.club.primaryColor}`;
    },
    successChartOptions() {
      return {
        chart: {
          type: 'pie'
        },
        labels: [this.$t('dashboard.charts.successRate.success'), this.$t('dashboard.charts.successRate.failure')],
        colors: [this.clubColor, '#e0e0e0'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + '%'
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + '%'
            }
          }
        }
      };
    },
    riskChartOptions() {
      return {
        chart: {
          type: 'pie'
        },
        labels: ['Reward', 'Risk'],
        colors: [this.clubColor, '#e0e0e0'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return val.toFixed(1) + '%'
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + '%'
            }
          }
        }
      };
    },
    histogramOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: {
            show: false // Hide the export option
          }
        },
        plotOptions: {
          bar: {
            borderRadius: 4,
            horizontal: false
          }
        },
        dataLabels: {
          enabled: false
        },
        xaxis: {
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        },
        colors: [this.clubColor, '#66DA26', '#546E7A', '#FF4560']
      };
    },
    histogramSeries() {
      return [{
        name: 'Profit',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]
      }];
    },
    lastSixMonths() {
      const months = [];
      const currentDate = new Date();

      for (let i = 0; i < 6; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        months.push({
          text: this.formatMonth(date),
          value: date.toISOString()
        });
      }

      return months;
    },
    getTop5AssetsPercentages() {
      if (!this.dataForMetricsAndCharts.mostUsedAssets) return [];
      return this.dataForMetricsAndCharts.mostUsedAssets
        .slice(0, 5)
        .map(asset => Math.round(asset.usagePercentage));
    },
    getTop5AssetLabels() {
      if (!this.dataForMetricsAndCharts.mostUsedAssets) return [];
      return this.dataForMetricsAndCharts.mostUsedAssets
        .slice(0, 5)
        .map(asset => asset.ticker);
    },
    topAssetsChartOptions() {
      return {
        chart: {
          type: 'pie'
        },
        labels: this.getTop5AssetLabels,
        colors: [this.clubColor, '#66DA26', '#546E7A', '#FF4560', '#FEB019'],
        legend: {
          position: 'bottom'
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return Math.round(val) + '%'
          }
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return Math.round(val) + '%'
            }
          }
        }
      };
    },
    hasAssetsData() {
      return this.dataForMetricsAndCharts.mostUsedAssets &&
        this.dataForMetricsAndCharts.mostUsedAssets.length > 0;
    }
  },
  methods: {
    formatMonth(date) {
      const monthNames = {
        en: ['January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'],
        es: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
          'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
      };

      const lang = this.$i18n.locale;
      const month = monthNames[lang][date.getMonth()];
      const year = date.getFullYear();

      return `${month} ${year}`;
    },
    async fetchPerformanceOverview() {
      this.isLoading = true;
      try {
        if (this.selectedAccount == 'All') {
          this.selectedAccount = 'LIVE,DEMO';
        }
        await Repository.getClubPerformanceOverview(this.$store.state.clubMember.club.id, this.selectedMonth, this.selectedAccount.toLocaleUpperCase())
          .then((response) => {
            this.dataForMetricsAndCharts = response.data;
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error(error);
          });
      } finally {
        this.isLoading = false;
      }
    }
  },
  async mounted() {
    this.selectedMonth = '1'; // Set default selected month to 1 month ago
    this.setLoading(true);
    await this.fetchPerformanceOverview();
    this.setLoading(false);
  },
  watch: {
    async selectedMonth() {
      await this.fetchPerformanceOverview();
    },
    async selectedAccount() {
      await this.fetchPerformanceOverview();
    }
  }
}
</script>



<style scoped lang="scss">
.dashboard-charts {
  padding: 20px;
  padding-left: 0;
}

.charts-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.dashboard-title {
  font-size: 1.8rem;
  font-weight: 600;
  color: var(--v-mainBlack-base)
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.metric-card {
  display: flex;
  align-items: center;
  padding: 10px;
  background: var(--v-fullWhite-base);
  border-radius: 12px;
  border: 1px solid #9a9a9a;
  transition: transform 0.2s;
  position: relative;
  /* Add this line */

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 5px;
    background-color: #9a9a9a;
    /* Use the club color */
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }
}

.metric-card:hover {
  transform: translateY(-2px);
}

.metric-icon {
  display: none;
  padding: 12px;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.03);
  margin-right: 16px;
}

.metric-content {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  min-height: 48px; // Add this to prevent layout shift when loading
}

.metric-label {
  margin: 0;
  font-size: 14px;
  margin-left: 15px;
  margin-right: 10px;
}

.metric-value {
  font-size: 32px;
  font-weight: 600;
  margin-right: 15px;
  margin-left: auto;
}

.circular-charts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.chart-card,
.histogram-card {
  background: var(--v-fullWhite-base);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #9a9a9a;
}

.blurred-chart apexchart {
  filter: blur(5px);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
}

.chart-actions {
  display: flex;
  gap: 8px;
}

.period-selector {
  display: flex;
  align-items: center;
}

.month-selector {
  width: 200px;
}

.no-data-message {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 16px;
  text-align: center;
}

@media (max-width: 768px) {
  .circular-charts {
    grid-template-columns: 1fr;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>
